/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { Icon, Tooltip } from 'antd';
import './SectionTrail.scss';

const STATUS_LABEL = {
  complete: 'Completed',
  'in-progress': 'In Progress',
  active: 'Current',
  incomplete: 'Not started',
};

const STATUS_ARIA = {
  complete: ' — completed',
  'in-progress': ' — in progress',
  active: ' — current',
  incomplete: '',
};

/** Convert a 6-digit hex color to rgba. */
function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function getConnectorStatus(leftStatus, rightStatus) {
  if (leftStatus === 'incomplete' || rightStatus === 'incomplete') return 'incomplete';
  return 'complete';
}

function SectionTrail({
  sections,
  ariaLabel = 'Section progress',
  history,
  loading = false,
  onStepClick,
}) {
  if (!loading && (!sections || !sections.length)) return null;
  const steps = sections || [];
  const currentIndex = steps.findIndex((s) => s.status === 'active');

  return (
    <div
      className={classNames('section-trail', { 'section-trail--loading': loading })}
      role="progressbar"
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
      aria-valuenow={!loading && currentIndex >= 0 ? currentIndex + 1 : undefined}
      aria-valuemax={!loading ? steps.length : undefined}
    >
      {steps.map((section, i) => {
        const { key, label, short, color, status } = section;
        const isClickable =
          !loading && status !== 'active' && (onStepClick ? section.navigable !== false : Boolean(section.route));

        const prevStatus = i > 0 ? steps[i - 1].status : null;
        const nextStatus = i < steps.length - 1 ? steps[i + 1].status : null;
        const beforeLineStatus = getConnectorStatus(prevStatus, status);
        const afterLineStatus = getConnectorStatus(status, nextStatus);

        let dotStyle = {};
        let labelStyle = {};

        if (!loading) {
          if (status === 'complete') {
            dotStyle = {
              background: hexToRgba(color, 0.14),
              borderColor: hexToRgba(color, 0.5),
              color,
            };
            labelStyle = { color: hexToRgba(color, 0.75) };
          } else if (status === 'in-progress') {
            dotStyle = {
              background: hexToRgba(color, 0.08),
              borderColor: color,
              color,
            };
            labelStyle = { color };
          } else if (status === 'active') {
            dotStyle = {
              background: color,
              borderColor: color,
              color,
              '--step-glow': hexToRgba(color, 0.22),
            };
            labelStyle = { color };
          }
        }

        const stepButton = (
          <button
            type="button"
            className={classNames(
              'section-trail__item',
              loading ? 'section-trail__item--incomplete' : `section-trail__item--${status}`,
            )}
            aria-current={!loading && status === 'active' ? 'step' : undefined}
            aria-label={loading ? short : `Go to ${label}${STATUS_ARIA[status]}`}
            onClick={
              isClickable
                ? () => {
                    if (onStepClick) onStepClick(section, i);
                    else if (section.route) history.push(section.route);
                  }
                : undefined
            }
            disabled={!isClickable}
            tabIndex={status === 'active' ? -1 : undefined}
          >
            <div className="section-trail__track" aria-hidden="true">
              <div
                className={classNames('section-trail__line', 'section-trail__line--before', {
                  'section-trail__line--hidden': i === 0,
                  [`section-trail__line--${beforeLineStatus}`]: i > 0 && !loading,
                  'section-trail__line--incomplete': i > 0 && loading,
                })}
              />
              <div className="section-trail__dot" style={dotStyle}>
                {!loading && status === 'complete' && <Icon type="check" />}
                {!loading && status === 'in-progress' && <div className="section-trail__progress-pip" />}
                {!loading && status === 'active' && <div className="section-trail__active-pip" />}
              </div>
              <div
                className={classNames('section-trail__line', 'section-trail__line--after', {
                  'section-trail__line--hidden': i === steps.length - 1,
                  [`section-trail__line--${afterLineStatus}`]: i < steps.length - 1 && !loading,
                  'section-trail__line--incomplete': i < steps.length - 1 && loading,
                })}
              />
            </div>
            <span className="section-trail__label" style={labelStyle}>
              {short}
            </span>
          </button>
        );

        return (
          <Tooltip
            key={key}
            title={
              loading ? (
                false
              ) : (
                <span>
                  {label}
                  <span
                    className={`section-trail__tooltip-status section-trail__tooltip-status--${status.replace('-', '')}`}
                  >
                    {STATUS_LABEL[status]}
                  </span>
                </span>
              )
            }
            placement="bottom"
            mouseEnterDelay={0.3}
            overlayClassName="section-trail__tooltip"
          >
            <span className="section-trail__tooltip-wrap">{stepButton}</span>
          </Tooltip>
        );
      })}
    </div>
  );
}

SectionTrail.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      short: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      route: PropTypes.string,
      navigable: PropTypes.bool,
      status: PropTypes.oneOf(['complete', 'in-progress', 'active', 'incomplete']).isRequired,
    }),
  ),
  ariaLabel: PropTypes.string,
  loading: PropTypes.bool,
  onStepClick: PropTypes.func,
};

export default withRouter(SectionTrail);
